import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bookmark, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { identify, track, trackServer, getUtmParams } from '../lib/analytics';

const API = '/api';

// Consume the post-auth redirect target stashed by a "Sign up to save"
// CTA (or similar). Falls back to the supplied default. Only accepts
// same-origin relative paths to prevent open-redirect attacks.
const _consumePostAuthNext = (fallback) => {
    try {
        const next = sessionStorage.getItem('brainsync_post_auth_next');
        sessionStorage.removeItem('brainsync_post_auth_next');
        if (next && next.startsWith('/') && !next.startsWith('//')) {
            return next;
        }
    } catch (_) { /* sessionStorage disabled */ }
    return fallback;
};

const GoogleAuthCallback = () => {
    const navigate = useNavigate();
    const { commitAuthenticatedUser } = useAuth();
    const hasProcessed = useRef(false);
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            const params = new URLSearchParams(window.location.search);

            const error = params.get('error');
            if (error) {
                // Silent sign-in failed (e.g. interaction_required) - try visible login with hint
                const savedEmail = localStorage.getItem('brainsync_user_email');
                if (error === 'interaction_required' && savedEmail) {
                    const redirectUri = window.location.origin + '/auth/google';
                    const scope = 'openid email profile';
                    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&prompt=select_account&login_hint=${encodeURIComponent(savedEmail)}`;
                    window.location.href = url;
                    return;
                }
                setErrorMsg(`Google returned error: ${error}`);
                setTimeout(() => navigate('/'), 4000);
                return;
            }

            let code = params.get('code');
            if (!code) {
                setErrorMsg('No authorization code returned from Google.');
                setTimeout(() => navigate('/'), 3000);
                return;
            }

            // Fix double-encoding
            if (code.includes('%')) {
                try { code = decodeURIComponent(code); } catch (_) {}
            }

            const redirectUri = window.location.origin + '/auth/google';

            // Pick up any referral code the user was tagged with before login.
            // The landing page stores it in localStorage when `?ref=...` is in the URL.
            let ref = null;
            try { ref = localStorage.getItem('brainsync_ref') || null; } catch (_) {}

            try {
                const response = await fetch(`${API}/auth/google/callback`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ code, redirect_uri: redirectUri, ref })
                });

                if (!response.ok) {
                    let detail = '';
                    try {
                        const err = await response.json();
                        detail = err?.detail || JSON.stringify(err).slice(0, 200);
                    } catch (_) {
                        detail = await response.text().catch(() => '');
                    }
                    console.error('Auth callback failed:', response.status, detail);
                    setErrorMsg(`Login failed (${response.status}): ${detail || 'no detail'}`);
                    setTimeout(() => navigate('/'), 5000);
                    return;
                }

                const rawUserData = await response.json();
                localStorage.setItem('brainsync_user_email', rawUserData.email || '');
                // Clear the referral marker so we don't re-apply on every login
                try { localStorage.removeItem('brainsync_ref'); } catch (_) {}
                // Fix B follow-up (Aug 2026): route through the
                // AuthContext helper so any pending try-it claim runs
                // BEFORE the first setUser commit — otherwise the
                // committed user carries `onboarded_via_anon_save=false`
                // and OnboardingOverlay mounts-then-hides. Same fix as
                // the native Android + cookie session-restore paths.
                const userData = await commitAuthenticatedUser(rawUserData);

                // ── GA4 ── identify the session + emit a login / signup
                // event. `is_new_user` now comes back reliably in the
                // callback response (backend-derived at insert time —
                // no more racy /auth/me follow-up call). Historical
                // event name / params kept identical so the marketing
                // dashboard's historical comparisons still line up.
                try {
                    identify(userData.user_id, {
                        plan_tier: userData.subscription_tier || 'free'
                    });
                    if (userData.is_new_user) {
                        // Server-side mirror as well — ad-blockers can't
                        // strip a same-origin call to /api/analytics/event.
                        trackServer('sign_up', { method: 'google', ...getUtmParams() });
                    } else {
                        track('login', { method: 'google' });
                    }
                } catch (_e) { /* swallow */ }

                // Verify the cookie actually propagated - some WebViews / proxies
                // strip Set-Cookie on redirects. If /api/auth/me doesn't accept
                // the cookie within a few retries, surface the error instead of
                // silently bouncing to the homepage.
                let cookieOK = false;
                for (let i = 0; i < 3; i++) {
                    await new Promise(r => setTimeout(r, 300 * (i + 1)));
                    try {
                        const meRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
                        if (meRes.ok) { cookieOK = true; break; }
                    } catch (_) { /* network blip */ }
                }

                if (!cookieOK) {
                    setErrorMsg('Signed in, but the session cookie did not persist. This usually means the browser blocked the cookie. Try clearing site data and signing in again.');
                    setTimeout(() => navigate('/'), 6000);
                    return;
                }

                const joinRedirect = sessionStorage.getItem('join_after_login');
                if (joinRedirect) {
                    sessionStorage.removeItem('join_after_login');
                    navigate(joinRedirect);
                    return;
                }

                // Resume a "Save to my BrainSync" action that was started
                // on a public profile while the user was logged out. We
                // stashed the bookmark_id + source username before
                // bouncing to sign-in; now that the session cookie is
                // in place, finish the clone and surface a confirmation
                // toast on the dashboard. Done BEFORE navigating so the
                // user lands on /dashboard with the bookmark already
                // saved (the existing dashboard-cache will pick it up
                // on its initial fetch).
                let pendingCloneRaw = null;
                try {
                    pendingCloneRaw = sessionStorage.getItem('bs_clone_after_login');
                } catch (_) { /* sessionStorage disabled (private mode) */ }
                if (pendingCloneRaw) {
                    try { sessionStorage.removeItem('bs_clone_after_login'); } catch (_) {}
                    let pending = null;
                    try { pending = JSON.parse(pendingCloneRaw); } catch (_) {}
                    // Ignore stale stashes (> 30 minutes) - the user
                    // probably abandoned the flow. 30min covers slow
                    // OAuth round-trips on flaky networks without
                    // resurrecting clicks from yesterday.
                    const isFresh = pending?.ts && (Date.now() - pending.ts) < 30 * 60 * 1000;
                    if (isFresh && pending.bookmark_id) {
                        try {
                            const cloneRes = await fetch(`${API}/share/${pending.bookmark_id}/clone`, {
                                method: 'POST',
                                credentials: 'include',
                            });
                            if (cloneRes.ok) {
                                const cloneData = await cloneRes.json().catch(() => ({}));
                                if (cloneData?.already_cloned) {
                                    toast.info('That bookmark is already in your BrainSync', {
                                        action: cloneData.bookmark_id
                                            ? { label: 'View', onClick: () => navigate(`/bookmark/${cloneData.bookmark_id}`) }
                                            : undefined,
                                    });
                                } else if (cloneData?.already_owned) {
                                    // The user signed in as the original
                                    // owner - nothing to clone, no toast
                                    // (would be confusing).
                                } else if (cloneData?.bookmark_id) {
                                    toast.success('Saved to your BrainSync', {
                                        action: { label: 'View', onClick: () => navigate(`/bookmark/${cloneData.bookmark_id}`) },
                                    });
                                }
                            } else {
                                toast.error('Could not save the shared bookmark - try again from the profile.');
                            }
                        } catch (_) {
                            toast.error('Network error while saving the shared bookmark.');
                        }
                    }
                }

                navigate(_consumePostAuthNext('/dashboard'));
            } catch (e) {
                console.error('Auth callback network error:', e);
                setErrorMsg(`Network error: ${e?.message || 'unknown'}`);
                setTimeout(() => navigate('/'), 5000);
            }
        };

        processAuth();
    }, [navigate, commitAuthenticatedUser]);

    if (errorMsg) {
        return (
            <div className="min-h-screen bg-primary flex items-center justify-center px-6" data-testid="auth-callback-error">
                <div className="text-center max-w-sm">
                    <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                        <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                    <p className="text-primary-foreground font-semibold mb-2">Sign-in failed</p>
                    <p className="text-primary-foreground/70 text-sm leading-relaxed">{errorMsg}</p>
                    <p className="text-primary-foreground/40 text-xs mt-4">Returning to home…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-primary flex items-center justify-center" data-testid="auth-callback">
            <div className="text-center animate-pulse">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                    <Bookmark className="w-8 h-8 text-primary-foreground" />
                </div>
                <p className="text-primary-foreground/70 font-mono text-sm">Signing you in...</p>
            </div>
        </div>
    );
};

export default GoogleAuthCallback;
